
import { Component, Vue } from "vue-property-decorator";
import DimssaButton from "@/components/shared/dimssa-button.vue";
import * as Models from "@gigalot/data-models";
import lodash from "lodash";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import BreadcrumbTrail from "@/components/BreadcrumbTrail.vue";
import { formatDetailBreadcrumbLabel, setupDetailBreadcrumbs } from "@/utils/breadcrumbs";

@Component({
  components: {
    DimssaButton,
    BreadcrumbTrail,
  },
  mixins: [validationMixin],
  validations: {
    formOwner: {
      name: { required },
      companies: {
        0: {
          name: { required },
        },
      },
    },
  },
})
export default class Owners extends Vue {
  owners: Models.CustomFeeder[] = [];
  formOwner: Models.CustomFeeder | null = null;
  originalOwner: Models.CustomFeeder | null = null;
  loadingOwners: boolean = false;
  savingOwner: boolean = false;
  errorMessage: string = "";
  changed: boolean = false;
  search: string = "";
  isNewOwner: boolean = true;

  ownerHeaders = [
    { text: "Owner", value: "name" },
    { text: "Company", value: "companies[0].name" },
    { text: "Active", value: "active" },
  ];

  get themeClass(): string {
    return this.$store.state.lightDarkMode;
  }

  get currentOwnerTitle(): string {
    return this.isNewOwner ? "New Owner" : "Edit Owner";
  }

  get primaryCompany(): Models.CustomFeederCompany {
    if (!this.formOwner) {
      return new Models.CustomFeederCompany();
    }

    if (!this.formOwner.companies || this.formOwner.companies.length === 0) {
      this.$set(this.formOwner, "companies", [new Models.CustomFeederCompany()]);
    }

    return this.formOwner.companies[0];
  }

  created() {
    this.initialiseFormOwnerFromStore();
    this.loadOwners();
  }

  initialiseFormOwnerFromStore() {
    const storedOwner = this.$store.getters["storage"]().owner as Models.CustomFeeder | undefined;
    if (storedOwner) {
      this.setFormOwner(this.cloneOwner(storedOwner), false);
    } else {
      this.startNewOwner();
    }
  }

  getOwnerRowClass(item: Models.CustomFeeder) {
    if (!this.formOwner) return "";
    return item.guid === this.formOwner.guid ? "selected-owner-row" : "";
  }

  getCompanyName(owner: Models.CustomFeeder): string {
    if (owner.companies && owner.companies.length > 0) {
      return owner.companies[0].name;
    }
    return "";
  }

  getCompanyVat(owner: Models.CustomFeeder): string {
    if (owner.companies && owner.companies.length > 0) {
      return owner.companies[0].vat;
    }
    return "";
  }

  lookupOwner(owner: string): string {
    if (!owner) return "";
    const match = lodash.find(this.owners, { guid: owner });
    return match ? match.name : owner;
  }

  onFieldChanged() {
    this.changed = true;
  }

  setFormOwner(owner: Models.CustomFeeder, isNew: boolean) {
    this.formOwner = owner;
    this.originalOwner = this.cloneOwner(owner);
    this.isNewOwner = isNew;
    this.changed = false;
    this.$store.commit("updateField", { path: "owner", value: this.cloneOwner(owner) });
  }

  startNewOwner() {
    const owner = this.createEmptyOwner();
    this.setFormOwner(owner, true);
  }

  selectOwner(owner: Models.CustomFeeder) {
    this.setFormOwner(this.cloneOwner(owner), false);
  }

  cloneOwner(owner: Models.CustomFeeder): Models.CustomFeeder {
    const clone = lodash.cloneDeep(owner);
    if (!clone.companies || clone.companies.length === 0) {
      clone.companies = [new Models.CustomFeederCompany()];
    }
    return clone;
  }

  createEmptyOwner(): Models.CustomFeeder {
    const owner = new Models.CustomFeeder();
    owner.companies = [new Models.CustomFeederCompany()];
    return owner;
  }

  async loadOwners() {
    this.loadingOwners = true;
    this.errorMessage = "";
    try {
      const gql = Models.gql.queries.customFeeders;
      const json = await this.$store.dispatch(
        "graphQl",
        {
          gql,
          variables: { guid: this.$store.state.user.location.guid },
        },
        { root: true }
      );
      this.owners = (json?.data?.customFeeders ?? []) as Models.CustomFeeder[];
      this.$store.commit("updateField", {
        path: "Owners",
        value: this.owners,
      });
      if (!this.formOwner && this.owners.length > 0) {
        this.selectOwner(this.owners[0]);
      }
    } catch (err) {
      console.log("loadOwners -> error", err);
      this.errorMessage = `Error downloading from server: ${err}`;
    } finally {
      this.loadingOwners = false;
    }
  }

  async saveOwner() {
    this.$v.$touch();
    if (this.$v.$invalid || !this.formOwner) return;

    this.savingOwner = true;
    try {
      const ownerToSave = this.cloneOwner(this.formOwner);
      ownerToSave.metadata = this.$store.getters["user/getUpstreamMetadata"]();

      await this.$store.dispatch(
        "graphQl",
        {
          gql: `mutation customFeeder($guid: String!, $customFeeder: CustomFeederInput!) {
            customFeeder(guid: $guid, customFeeder: $customFeeder)
          }`,
          variables: {
            guid: this.$store.state.user.location.guid,
            customFeeder: ownerToSave,
          },
        },
        { root: true }
      );

      await this.loadOwners();
      const savedOwner = this.owners.find((o) => o.guid === ownerToSave.guid);
      if (savedOwner) {
        this.selectOwner(savedOwner);
      } else {
        this.startNewOwner();
      }
      this.changed = false;
    } catch (err) {
      console.log("saveOwner -> error", err);
      this.$store.commit("popup/displayOk", {
        message: "Error occurred while trying to save to server",
      });
    } finally {
      this.savingOwner = false;
    }
  }

  cancel() {
    if (this.changed) {
      this.$store.commit("popup/displayYesNo", {
        message: "Discard changes?",
        yesAction: () => this.resetForm(),
      });
    } else {
      this.resetForm();
    }
  }

  resetForm() {
    if (this.originalOwner) {
      const resetOwner = this.cloneOwner(this.originalOwner);
      this.formOwner = resetOwner;
      this.changed = false;
      this.originalOwner = this.cloneOwner(resetOwner);
      this.$store.commit("updateField", { path: "owner", value: this.cloneOwner(resetOwner) });
    } else {
      this.startNewOwner();
    }
  }

  get breadcrumbs() {
    const detailLabel = formatDetailBreadcrumbLabel("Owner", this.formOwner?.name);
    return setupDetailBreadcrumbs({
      listText: "Owners",
      listRoute: "/list/owners",
      currentText: detailLabel,
    });
  }
}
